import React, { useEffect, useState, ReactNode, JSX } from 'react';
import { renderIcon } from 'src/constants/icons';
import BaseTour from 'src/components/common/tour/BaseTour';
import TourButton from 'src/components/common/tour/TourButton';
import {
  TourIntroduction,
  TourSection,
  TourParagraph,
  TourList,
  TourListItem,
  TourCompletion,
} from 'src/components/common/tour/TourContentTemplates';
import {
  tourStepStyles,
  checkTourNavigation,
  setTourNavigation,
  shouldStartImmediately,
} from 'src/components/common/tour/tourUtils';
import { tourActions } from 'src/components/common/tour/tourUtils';

const TOUR_STEPS = [
  {
    selector: '[data-tour="notifications-tour-button"]',
    content: (
      <TourIntroduction
        title="Notifications Tour Guide"
        description="Learn how to manage and filter notifications using the new notifications page."
        behaviorText=""
        autoPlayText="Auto-play: The tour auto-advances every 20 seconds. Use the X to exit or the navigation buttons to pause and move through steps on your own."
        additionalInfo=""
      />
    ),
  },
  {
    selector: '[data-tour="settings-icon"]',
    content: (
      <TourSection title="Notification Settings">
        <TourParagraph>
          Click the settings gear to choose which notifications you get and
          filter.
        </TourParagraph>
      </TourSection>
    ),
    position: 'left',
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        top: '150px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  {
    selector: '[data-tour="help-icon"]',
    content: (
      <TourSection title="Help & Information">
        <TourParagraph>
          Click the help icon to see what each notification type means.
        </TourParagraph>
        <TourParagraph>
          You&apos;ll also learn what actions cause each type of notification.
        </TourParagraph>
      </TourSection>
    ),
    position: 'left',
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        top: '150px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  {
    selector: '[data-tour="refresh-icon"]',
    content: (
      <TourSection title="Refresh Notifications">
        <TourParagraph>
          Click refresh to check for new alerts right away.
        </TourParagraph>
        <TourParagraph>
          Notifications usually update on their own within a few seconds.
        </TourParagraph>
      </TourSection>
    ),
    position: 'left',
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        top: '150px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  {
    selector: '[data-tour="filters-expand-button"]',
    content: (
      <TourSection title="Expand Filters">
        <TourParagraph>
          Click the expand arrow to open all filters.
        </TourParagraph>
        <TourParagraph>
          Use filters to narrow your notifications by type.
        </TourParagraph>
        <TourParagraph>
          A number next to each filter shows how many notifications are in that
          category.
        </TourParagraph>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
    action: () => {
      // Expand filters when this step is reached
      tourActions.expandFilters('[data-tour="filters-expand-button"]');
    },
  },
  {
    selector: '[data-tour="read-status-filters"]',
    content: (
      <TourSection title="Read Status Filters">
        <TourParagraph>
          Quickly filter notifications by their read status. Choose between
          showing:
        </TourParagraph>
        <TourList>
          <TourListItem icon={renderIcon('filter', 'all')}>
            <strong>All:</strong> All notifications regardless of read status
          </TourListItem>
          <TourListItem
            icon={renderIcon('filter', 'read', { color: 'success' })}
          >
            <strong>Read:</strong> Only read notifications
          </TourListItem>
          <TourListItem
            icon={renderIcon('filter', 'unread', { color: 'primary' })}
          >
            <strong>Unread:</strong> Only unread notifications
          </TourListItem>
        </TourList>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
  },
  {
    selector: '[data-tour="source-filters"]',
    content: (
      <TourSection title="Source Filters">
        <TourParagraph>
          Filter notifications by where they come from:
        </TourParagraph>
        <TourList>
          <TourListItem
            icon={renderIcon('filter', 'all', { color: 'primary' })}
          >
            <strong>All:</strong> File Owner, Watching, Property, and System
          </TourListItem>
          <TourListItem
            icon={renderIcon('header', 'fileOwner', {
              style: { color: '#1976d2' },
            })}
          >
            <strong>File Owner:</strong> Files where you&apos;re the assigned
            File Owner
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'watching', {
              style: { color: '#1976d2' },
            })}
          >
            <strong>Watching:</strong> Files on your watch list
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'property', {
              style: { color: '#1976d2' },
            })}
          >
            <strong>Property:</strong> Notifications tied to a property
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'system', {
              style: { color: '#1976d2' },
            })}
          >
            <strong>System:</strong> Pronto notices like outages or updates
          </TourListItem>
        </TourList>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
  },
  {
    selector: '[data-tour="event-type-filters"]',
    content: (
      <TourSection title="Event Type Filters">
        <TourParagraph>
          Filter by what happened on files you own or watch:
        </TourParagraph>
        <TourList>
          <TourListItem icon={renderIcon('filter', 'all')}>
            <strong>All:</strong> All types
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'email', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>Email:</strong> A new email was received
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'sms', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>SMS:</strong> A new text message was received
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'fax', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>Fax:</strong> A new fax was received
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'documents', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>Documents:</strong> A renter uploaded a new document
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'fileOwner', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>File Owner:</strong> You were assigned to a file
          </TourListItem>
          <TourListItem
            icon={renderIcon('notification', 'statusNote', {
              style: { color: '#2e7d32' },
            })}
          >
            <strong>Status Note:</strong> A new status note was added
          </TourListItem>
        </TourList>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
  },
  {
    selector: '[data-tour="notification-type-filters"]',
    content: (
      <TourSection title="Notification Type Filters">
        <TourParagraph>
          Filter notifications based on how many files each action affected.
        </TourParagraph>
        <TourParagraph>
          Use this to see whether a notification came from a single-file update
          or a bulk action:
        </TourParagraph>
        <TourList>
          <TourListItem
            icon={renderIcon('filter', 'all', { color: 'primary' })}
          >
            <strong>All:</strong> Show all
          </TourListItem>
          <TourListItem
            icon={renderIcon('filter', 'single', {
              sx: { color: 'text.primary' },
            })}
          >
            <strong>Single File:</strong> Notifications from actions on one
            file, like emails, document uploads, or status changes
          </TourListItem>
          <TourListItem
            icon={renderIcon('filter', 'bulk', {
              sx: { color: 'text.primary' },
            })}
          >
            <strong>Multiple Files:</strong> Notifications from bulk actions,
            like rent roll loads, mass file owner assignments, or Bulk
            Communications
          </TourListItem>
        </TourList>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
  },
  {
    selector: '[data-tour="pagination"]',
    content: (
      <TourSection title="Pagination">
        <TourParagraph>
          Use the page arrows to go back and see older notifications.
        </TourParagraph>
      </TourSection>
    ),
    ...tourStepStyles.leftPositionWide,
  },
  {
    selector: '[data-tour="notifications-tour-button"]',
    content: (
      <TourCompletion
        title="Notifications Tour Complete!"
        congratulations="You're all set!"
        summary="You've learned how to track and filter notifications."
        reminder=""
        nextStep="Now head to Settings to customize your notification preferences."
      />
    ),
    position: 'left',
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        top: '100px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
];

const NotificationsTour = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [shouldAutoStart, setShouldAutoStart] = useState(false);

  useEffect(() => {
    // Check if tour should start immediately (coming from header tour) or use normal logic
    const canAutoStart = shouldStartImmediately('notificationsTourData');

    if (canAutoStart) {
      // Check if coming from header tour and remove the flag
      const fromHeaderTour = checkTourNavigation('fromHeaderTour');

      if (fromHeaderTour) {
        // Start immediately when coming from header tour
        setShouldAutoStart(true);
      } else {
        // Otherwise, add 10-second delay for normal auto-start
        const timer = setTimeout(() => {
          setShouldAutoStart(true);
        }, 10000); // 10 seconds

        return () => clearTimeout(timer);
      }
    }

    // Ensure filters start collapsed when tour begins
    tourActions.collapseFilters('[data-tour="filters-expand-button"]');
  }, []);

  const handleTourEnd = (): void => {
    // Set a flag to indicate user is coming from notifications tour
    setTourNavigation('fromNotificationsTour');
    // Add a small delay to ensure localStorage is set before navigation
    setTimeout(() => {
      // Navigate to User Settings page
      tourActions.navigateTo('/workbench/user/settings');
    }, 100);
  };

  return (
    <BaseTour
      steps={TOUR_STEPS}
      tourDataKey="notificationsTourData"
      onTourEnd={handleTourEnd}
      initialIsOpen={shouldAutoStart}
    >
      {children}
    </BaseTour>
  );
};

// Export the TourButton component that uses the context
const NotificationsTourButton = (): JSX.Element => (
  <div data-tour="notifications-tour-button">
    <TourButton
      title="Start notifications tour"
      tourDataKey="notificationsTourData"
    />
  </div>
);

export { NotificationsTour, NotificationsTourButton as TourButton };
export default NotificationsTour;
